import { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { Play } from "lucide-react";

import { useSharedPlaylistParam } from "@/hooks/useUrlParams";
import { usePlaylistCoursesForPlaylist } from "@/hooks/usePlaylists";
import { useAsync } from "@/hooks/useAsync";
import * as api from "@/lib/api";
import { daysRemaining, isExpired } from "@/lib/helpers";
import type { PlaylistCourseWithDetails } from "@/lib/schema";
import { CommentSection } from "@/components/comments/CommentSection";
import { MusicMixSection } from "@/components/playlist/MusicMixSection";
import { CompleteRoutineSection } from "@/components/playlist/CompleteRoutineSection";
import { GuestNameModal } from "@/components/comments/GuestNameBanner";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/lib/supabase";

export function SharedPlaylistPage() {
  const { user, loading: authLoading } = useAuth();
  const shareToken = useSharedPlaylistParam();
  const trackedShareTokenRef = useRef<string | null>(null);

  // Resolve invitation token directly via a single-row query
  const {
    data: playlistShare,
    loading: sharedLoading,
    revalidate: revalidatePlaylistShare,
  } = useAsync(
    () => api.resolvePlaylistShareToken(shareToken!),
    shareToken ? [shareToken] : null,
  );

  const playlistId = playlistShare?.playlist_id?.id ?? null;
  const {
    data: playlistCoursesData,
    loading: coursesLoading,
    revalidate: revalidatePlaylistCourses,
  } = usePlaylistCoursesForPlaylist(playlistId);
  const playlistName = playlistShare?.playlist_id?.name ?? "Shared Performance";
  const organizerUserId = playlistShare?.playlist_id?.user_id ?? null;
  const playlistCourses = playlistCoursesData ?? [];

  const expiryInfo = useMemo(() => {
    if (!playlistShare?.expires_at) return null;
    if (isExpired(playlistShare.expires_at)) {
      return { expired: true, days: 0 };
    }
    const days = daysRemaining(playlistShare.expires_at);
    return { expired: false, days };
  }, [playlistShare]);

  const loading = sharedLoading || coursesLoading;

  useEffect(() => {
    if (!playlistId) return;

    let debounceTimeout: number | null = null;
    let fallbackInterval: number | null = null;

    const revalidateAll = () =>
      Promise.all([
        revalidatePlaylistShare(),
        revalidatePlaylistCourses(),
      ]);

    const clearFallbackInterval = () => {
      if (fallbackInterval !== null) {
        window.clearInterval(fallbackInterval);
        fallbackInterval = null;
      }
    };

    const ensureFallbackInterval = () => {
      if (fallbackInterval !== null) return;
      fallbackInterval = window.setInterval(() => {
        void revalidateAll();
      }, 30_000);
    };

    const scheduleRevalidate = () => {
      if (debounceTimeout !== null) {
        window.clearTimeout(debounceTimeout);
      }
      debounceTimeout = window.setTimeout(() => {
        debounceTimeout = null;
        void revalidateAll();
      }, 250);
    };

    const channel = supabase
      .channel(`shared-playlist:${playlistId}:${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "playlist_courses",
          filter: `playlist_id=eq.${playlistId}`,
        },
        scheduleRevalidate,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "playlists",
          filter: `id=eq.${playlistId}`,
        },
        scheduleRevalidate,
      );

    ensureFallbackInterval();
    channel.subscribe((status) => {
      if (status === "SUBSCRIBED") {
        clearFallbackInterval();
        return;
      }

      if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") {
        ensureFallbackInterval();
      }
    });

    return () => {
      if (debounceTimeout !== null) {
        window.clearTimeout(debounceTimeout);
      }
      clearFallbackInterval();
      void supabase.removeChannel(channel);
    };
  }, [playlistId, revalidatePlaylistCourses, revalidatePlaylistShare]);

  useEffect(() => {
    if (
      !shareToken ||
      trackedShareTokenRef.current === shareToken ||
      authLoading ||
      !!user ||
      loading ||
      !playlistShare ||
      expiryInfo?.expired
    ) {
      return;
    }

    trackedShareTokenRef.current = shareToken;
    void api.trackShareOpen("playlist", shareToken);
  }, [authLoading, user, shareToken, loading, playlistShare, expiryInfo?.expired]);

  const defaultInstructorImage =
    "https://cdn.prod.website-files.com/686229256a807206c1874545/687bcf08468fe4993282a9ec_Khusbu.avif";

  if (!shareToken) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-50 px-4">
        <div className="text-center">
          <p className="text-lg font-semibold text-neutral-900">Invalid Share Link</p>
          <p className="mt-2 text-sm text-neutral-500">No invitation token was provided.</p>
          <Link to="/" className="mt-4 inline-block text-sm text-[#762a5c] hover:underline">
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f6f5f6]">
        <div className="mx-auto w-full max-w-[80rem] px-[clamp(1rem,3vw,3rem)] py-12">
          <div className="mb-8 h-10 w-64 animate-pulse rounded bg-neutral-200" />
          <div className="grid grid-cols-[repeat(auto-fill,minmax(min(26rem,100%),1fr))] gap-[1.75rem]">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="h-80 animate-pulse rounded-[1.2rem] bg-neutral-200"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!playlistShare) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f6f5f6] px-4">
        <div className="text-center">
          <p className="text-lg font-semibold text-[#321b29]">Performance Not Found</p>
          <p className="mt-2 text-[#70696f]">This shared performance could not be found.</p>
          <Link to="/" className="mt-4 inline-block text-sm text-[#762a5c] hover:underline">
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  if (expiryInfo?.expired) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f6f5f6] px-4">
        <div className="text-center">
          <p className="text-lg font-semibold text-[#ba4d4d]">This share link has expired</p>
          <p className="mt-2 text-[#70696f]">Ask the owner to share a new link.</p>
          <Link to="/" className="mt-4 inline-block text-sm text-[#762a5c] hover:underline">
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f5f6]">
      {!user && <GuestNameModal />}
      <div className="mx-auto w-full max-w-[80rem] px-[clamp(2rem,5vw,5rem)] pb-12 pt-[clamp(3rem,6vw,6rem)] max-[767px]:pt-8 max-[767px]:pb-6">
        {/* Playlist name */}
        <h1 className="m-0 mb-8 font-[family-name:var(--font-display)] text-[clamp(1.5rem,1.2143rem+1.4286vw,2.5rem)] font-medium leading-[1.2] text-[#321b29]">
          {playlistName}
        </h1>

        {/* Course cards grid */}
        {playlistCourses.length > 0 ? (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(min(26rem,100%),1fr))] gap-[1.75rem]">
            {playlistCourses.map((pc, idx) => (
              <SharedCourseCard
                key={pc.id}
                pc={pc}
                sequenceNumber={idx + 1}
                shareToken={shareToken}
                instructorImage={defaultInstructorImage}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-[1rem] border border-[#e2e4e9] bg-white p-[1.35rem]">
            <p className="text-[0.96rem] text-[#70696f]">
              This performance has no tutorials yet.
            </p>
          </div>
        )}

        <CompleteRoutineSection playlistId={playlistId!} shareToken={shareToken} className="mt-8" />
        {playlistShare.playlist_id?.music_url && (
          <MusicMixSection
            playlistId={playlistId!}
            musicUrl={playlistShare.playlist_id.music_url}
            className="mt-8"
          />
        )}

        <CommentSection
          playlistId={playlistId}
          guestToken={shareToken}
          guestTokenType="playlist"
          organizerUserId={organizerUserId}
          className="mt-8"
        />
      </div>
    </div>
  );
}

function SharedCourseCard({
  pc,
  sequenceNumber,
  shareToken,
  instructorImage,
}: {
  pc: PlaylistCourseWithDetails;
  sequenceNumber: number;
  shareToken: string;
  instructorImage: string;
}) {
  const courseId = pc.course_id.id;
  const courseName = pc.course_id.name;
  const thumbnail = pc.course_id.thumbnail_links_course;
  const instructorName = pc.course_id.Instructor?.name ?? "Khusbu Gemmati";
  const courseHref = `/app/course-shared?shared_playlist=${shareToken}&shared_playlist_course=${courseId}`;

  return (
    <article className="relative flex h-full w-full max-w-[38rem] flex-col overflow-visible rounded-[1.2rem] border border-[#dfdbde] bg-[#fafafa] shadow-[inset_0_1px_0_rgba(255,255,255,0.9)]">
      <div className="absolute -left-3 top-0 z-10 -translate-y-1/2 flex h-[1.85rem] w-[1.85rem] items-center justify-center rounded-full bg-[#f3e8ef] text-[0.82rem] font-bold text-[#762a5c] shadow-[0_0_0_4px_#f6f5f6]">
        {sequenceNumber}
      </div>
      <Link
        to={courseHref}
        className="relative m-[0.9rem] block aspect-[1.75] overflow-hidden rounded-[1rem] bg-[#ece7ea]"
      >
        {thumbnail ? (
          <img
            src={thumbnail}
            alt={courseName}
            className="block h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="block h-full w-full bg-[linear-gradient(135deg,#ffb239_0%,#f4a232_45%,#f0ecef_45%,#f0ecef_100%)]" />
        )}
        <div className="absolute left-1/2 top-1/2 inline-flex h-[3.5rem] w-[3.5rem] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/[0.38] text-white backdrop-blur-[8px]">
          <Play className="h-5 w-5" />
        </div>
      </Link>

      <div className="flex items-center gap-4 px-[0.95rem] pb-[0.95rem] pt-[0.1rem]">
        <div className="flex min-w-0 items-center gap-[0.65rem]">
          <div className="h-[1.65rem] w-[1.65rem] flex-none overflow-hidden rounded-[0.3rem]">
            <img
              src={instructorImage}
              alt={instructorName}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="min-w-0">
            <div className="text-[0.58rem] font-bold uppercase tracking-[0.25em] text-[#b0a8ad]">
              Instructor
            </div>
            <div className="text-[0.82rem] font-bold leading-[1.15] text-[#3a3338]">
              {instructorName}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-[#ebe7ea] px-[0.95rem] pb-[1.25rem] pt-[1.1rem]">
        <Link
          to={courseHref}
          className="inline-block font-[var(--font-display)] text-[1.15rem] leading-[1.2] text-[#321b29]"
        >
          {courseName}
        </Link>
      </div>

      {pc.notes && (
        <div className="flex flex-1 flex-col border-t border-[#ebe7ea] px-[0.95rem] pb-[1.1rem] pt-[0.85rem]">
          <div className="mb-1 text-[0.6rem] font-bold uppercase tracking-[0.2em] text-[#b0a8ad]">
            Note
          </div>
          <p className="m-0 whitespace-pre-wrap text-[0.85rem] leading-[1.45] text-[#4a3f47]">
            {pc.notes}
          </p>
        </div>
      )}
    </article>
  );
}
